import React, { useState } from 'react';

import { RemovableDashedTag } from '@/components/RemovableDashedTag';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useGetCapabilities } from '@/hooks/api/useGetCapabilities';
import { SelectionTable } from '@/sections/attacks/attackPaths/components/wizards/components/SelectionTable';
import { WizardSearchInput } from '@/sections/attacks/attackPaths/components/wizards/components/WizardSearchInput';
import type { SearchableListItem } from '@/sections/attacks/attackPaths/components/wizards/hooks/useSearchableList';
import { type CatoAutoTriageConfig } from '@/sections/settings/components/forms/catoAutoTriageConstants';

export { type CatoAutoTriageConfig } from '@/sections/settings/components/forms/catoAutoTriageConstants';

const EXTRA_SOURCES: { value: string; label: string }[] = [
  { value: 'hannibal', label: 'Hannibal' },
  { value: 'romulus-agent', label: 'Romulus' },
];

const SEVERITY_OPTIONS = [
  { value: 'TC', label: 'Critical' },
  { value: 'TH', label: 'High' },
  { value: 'TM', label: 'Medium' },
  { value: 'TL', label: 'Low' },
];

const sourceColumns: import('@tanstack/react-table').ColumnDef<SearchableListItem>[] =
  [
    {
      id: 'name',
      accessorFn: row => row.name,
      header: 'Capability',
      cell: ({ row }) => (
        <span className="text-chariot-text-primary truncate text-sm">
          {row.original.name}
        </span>
      ),
    },
  ];

interface CatoAutoTriageStepProps {
  config: CatoAutoTriageConfig;
  onChange: (config: CatoAutoTriageConfig) => void;
  disabled?: boolean;
}

export function CatoAutoTriageStep({
  config,
  onChange,
  disabled,
}: CatoAutoTriageStepProps) {
  const [searchTerm, setSearchTerm] = useState('');

  const { capabilities } = useGetCapabilities();
  const sourceSuggestions = React.useMemo<
    { value: string; label: string }[]
  >(() => {
    const fromRegistry = (capabilities ?? []).map(c => ({
      value: c.name,
      label: c.title || c.name,
    }));
    const seen = new Set(fromRegistry.map(s => s.value));
    const extras = EXTRA_SOURCES.filter(s => !seen.has(s.value));
    return [...fromRegistry, ...extras].sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [capabilities]);

  const resolveLabel = (value: string) =>
    sourceSuggestions.find(s => s.value === value)?.label ?? value;

  const toggleSource = (key: string) =>
    onChange({
      ...config,
      sources: config.sources.includes(key)
        ? config.sources.filter(s => s !== key)
        : [...config.sources, key],
    });

  const toggleSeverity = (value: string) =>
    onChange({
      ...config,
      severities: config.severities.includes(value)
        ? config.severities.filter(s => s !== value)
        : [...config.severities, value],
    });

  const filteredItems: SearchableListItem[] = sourceSuggestions
    .filter(
      opt =>
        !searchTerm ||
        opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        opt.value.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .map(opt => ({ key: opt.value, name: opt.label }) as SearchableListItem);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="border-chariot-stroke bg-chariot-background-secondary flex items-center justify-between gap-4 rounded-lg border px-4 py-3">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="cato-auto-triage-enabled"
            className="text-chariot-text-primary text-sm font-medium"
          >
            Automatic finding validation
          </label>
          <span className="text-chariot-text-secondary text-xs">
            Cato automatically validates new findings that match these criteria.
            Manual runs always use the Marcus budget.
          </span>
        </div>
        <Switch
          id="cato-auto-triage-enabled"
          checked={config.enabled}
          onCheckedChange={checked => onChange({ ...config, enabled: checked })}
          disabled={disabled}
          data-testid="cato-auto-triage-enabled"
        />
      </div>

      {config.enabled && (
        <>
          <div className="flex items-center justify-between">
            <span className="text-chariot-text-primary text-sm font-medium">
              Capability sources
            </span>
            <div className="flex items-center gap-3">
              <div className="flex gap-1">
                {SEVERITY_OPTIONS.map(opt => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={
                      config.severities.includes(opt.value)
                        ? 'default'
                        : 'outline'
                    }
                    size="sm"
                    onClick={() => toggleSeverity(opt.value)}
                    disabled={disabled}
                    data-testid={`cato-severity-${opt.value}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              <span
                className="text-chariot-text-secondary shrink-0 text-xs"
                aria-live="polite"
              >
                {config.sources.length} selected
              </span>
            </div>
          </div>

          {config.sources.length > 0 && (
            <div className="border-chariot-stroke flex flex-wrap gap-1.5 border-b pb-3">
              {config.sources.map(src => (
                <RemovableDashedTag
                  key={src}
                  label={resolveLabel(src)}
                  size="xs"
                  onRemove={() => toggleSource(src)}
                />
              ))}
            </div>
          )}

          <WizardSearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search capabilities..."
            ariaLabel="Search capabilities to include"
          />

          <div className="flex min-h-0 flex-1 flex-col">
            <SelectionTable
              mode="multi"
              data={filteredItems}
              columns={sourceColumns}
              selectedKeys={new Set(config.sources)}
              onToggle={item => toggleSource(item.key)}
              fillHeight
            />
          </div>
        </>
      )}
    </div>
  );
}
